import React, { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import ChatMarkdown from './ChatMarkdown';
import { apiService, BriefBuilderMessage } from '../services/api';

interface BriefTranscriptModalProps {
  briefId: string;
  workspaceName: string;
  onClose: () => void;
}

type TranscriptPart =
  | { type: 'text'; content: string }
  | { type: 'structured'; label: string; content: string };

const STRUCTURED_BLOCK_REGEX =
  /\[(Context|Competitor|Output)_Start\]([\s\S]*?)\[\1_End\]|\[Prompt_Brief_For_Research\]([\s\S]*?)\[Prompt_End\]/gi;

const parseTranscriptParts = (content: string): TranscriptPart[] => {
  const parts: TranscriptPart[] = [];
  let cursor = 0;
  STRUCTURED_BLOCK_REGEX.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = STRUCTURED_BLOCK_REGEX.exec(content)) !== null) {
    const matchIndex = match.index;
    const precedingText = content.slice(cursor, matchIndex).trim();
    if (precedingText) parts.push({ type: 'text', content: precedingText });

    const blockType = match[1]?.toLowerCase();
    const blockContent = String(match[2] || match[3] || '').trim();
    if (blockContent) {
      const label =
        blockType === 'context'
          ? 'Context'
          : blockType === 'competitor'
            ? 'Competitors'
            : blockType === 'output'
              ? 'Research outline'
              : 'Research prompt';
      parts.push({ type: 'structured', label, content: blockContent });
    }
    cursor = matchIndex + match[0].length;
  }

  const remainingText = content.slice(cursor).trim();
  if (remainingText) parts.push({ type: 'text', content: remainingText });
  return parts;
};

/**
 * Read-only viewer for the brief-builder (staging) conversation that
 * produced this workspace. Fetches lazily when opened; never blocks or
 * affects any workspace state.
 */
const BriefTranscriptModal: React.FC<BriefTranscriptModalProps> = ({
  briefId,
  workspaceName,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<BriefBuilderMessage[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.fetchBriefBuilderSession(briefId);
        if (cancelled) return;
        setMessages(data.messages || []);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError('The prompt conversation for this workspace could not be loaded.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [briefId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <>
      <div className="brief-transcript-modal-overlay" onClick={onClose} />
      <div className="brief-transcript-modal" role="dialog" aria-modal="true" aria-label="Prompt conversation">
        <div className="brief-transcript-modal__panel">
          <div className="brief-transcript-modal__header">
            <div>
              <h3>{workspaceName.trim() || 'Workspace'} - Problem Framing Conversation</h3>
              <p>The staging conversation used to prepare this workspace. Read-only.</p>
            </div>
            <button
              type="button"
              className="brief-transcript-modal__close"
              onClick={onClose}
              aria-label="Close prompt conversation"
            >
              <X size={16} />
            </button>
          </div>

          <div className="brief-transcript-modal__body">
            {loading && (
              <div className="brief-transcript-modal__status">
                <Loader2 className="ca-spin" size={16} />
                <span>Loading conversation…</span>
              </div>
            )}

            {!loading && error && (
              <div className="brief-transcript-modal__status brief-transcript-modal__status--error">
                {error}
              </div>
            )}

            {!loading && !error && (
              <>
                {messages.length === 0 ? (
                  <div className="brief-transcript-modal__status">
                    No conversation was recorded for this workspace.
                  </div>
                ) : (
                  <div className="brief-thread">
                    {messages.map((msg, idx) => {
                      const parts = msg.role === 'assistant'
                        ? parseTranscriptParts(msg.content)
                        : [{ type: 'text' as const, content: msg.content }];
                      return (
                        <div
                          key={`${msg.timestamp}-${idx}`}
                          className={`brief-message brief-message--${msg.role} brief-message--plain`}
                        >
                          <div className="brief-message__label">
                            {msg.role === 'user' ? 'You' : 'Astra'}
                          </div>
                          <div className="brief-message__content">
                            {parts.map((part, partIndex) =>
                              part.type === 'structured' ? (
                                <div
                                  key={partIndex}
                                  className="brief-transcript-modal__structured-block"
                                >
                                  <div className="brief-transcript-modal__structured-label">
                                    {part.label}
                                  </div>
                                  <ChatMarkdown
                                    text={part.content}
                                    className="structured-markdown"
                                  />
                                </div>
                              ) : msg.role === 'assistant' ? (
                                <ChatMarkdown
                                  key={partIndex}
                                  text={part.content}
                                  className="structured-markdown"
                                />
                              ) : (
                                <React.Fragment key={partIndex}>{part.content}</React.Fragment>
                              )
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BriefTranscriptModal;
